/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    getPayloadObject,
    getTelemetryAttributes,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
// VSI Customization - starts
import classnames from 'classnames';
// VSI Customization - ends
import * as React from 'react';
import Cookies from 'universal-cookie';

// VSI Customization - starts
export interface IStoreSelectorSearchFormState {
    isError?: boolean;
}
// VSI Customization - ends
export interface IStoreSelectorSearchFormProps {
    resources: {
        searchInputAriaLabel: string;
        searchButtonAriaLabel: string;
        searchPlaceholderText: string;
        seeAllStoresText: string;
        viewListText: string;
        viewMapText: string;
        // VSI Customization - starts
        searchButtonText: string;
        searchLabelText: string;
        // VSI Customization - ends
    };
    // VSI Customization - starts
    isError: boolean;
    id: string;
    value: string;
    isMapDisabled?: boolean;
    // showAllStores?: boolean;
    displayList?: boolean;
    locatorView?: boolean;
    /** The telemetry content */
    telemetryContent?: ITelemetryContent;
    // onShowAllStores(): void;
    onToggleListMapViewState(): void;
    performSearch(searchTerm: string): Promise<void>;
    searchTermChanged(searchTerm: string): Promise<void>;
    isErrorChanged(isError: boolean): Promise<void>;
    // VSI Customization - ends
}

/**
 * Simple search form consisting of search text and a search button
 */
export class StoreSelectorSearchForm extends React.PureComponent<IStoreSelectorSearchFormProps & IStoreSelectorSearchFormState> {
    private searchBoxRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();
    private storeSearchAttributes: Msdyn365.IDictionary<string> | undefined;
    private toggleMapViewAttributes: Msdyn365.IDictionary<string> | undefined;

    // VSI Customization - starts
    constructor(props: IStoreSelectorSearchFormProps & IStoreSelectorSearchFormState) {
        super(props);
        const cookies = new Cookies();
        const zipCode = cookies.get('zip_code');
        this.state = { value: zipCode, isError: true };
        const payLoad: IPayLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.SearchStore);
        this.storeSearchAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ShowAllStore;
        // this.showAllStoreAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
        payLoad.contentAction.etext = TelemetryConstant.ToggleMapView;
        this.toggleMapViewAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);
    }

    public render(): JSX.Element {
        const {
            resources: { searchButtonAriaLabel, searchInputAriaLabel, searchButtonText, viewListText, viewMapText, searchLabelText },
            id,
            value,
            isError,
            isMapDisabled,
            displayList,
            locatorView,
            onToggleListMapViewState
        } = this.props;
        // VSI Customization - ends

        const toggleButtonText = displayList ? viewMapText : viewListText;

        return (
            <div className='ms-store-select__search'>
                <form
                    className='ms-store-select__search-form'
                    aria-label={searchButtonAriaLabel}
                    name='storeSelectorSearchForm'
                    role='form'
                    autoComplete='off'
                    onSubmit={this._onSubmit}
                    id={`ms-store-select__search-box-container_${id}`}
                >
                    <input
                        type='text'
                        aria-label={searchInputAriaLabel}
                        // VSI Customization - starts
                        className={classnames(
                            'msc-form-control ms-store-select__search-input field-control',
                            value.length > 0 && 'field-control--value',
                            isError && 'field-control--error'
                        )}
                        value={value}
                        onChange={this._searchTextChanged}
                        id={`ms-store-select__search-box_${id}`}
                        ref={this.searchBoxRef}
                        role='combobox'
                        aria-expanded='false'
                        aria-controls='as_containerSearch'
                        maxLength={5}
                    />
                    <label className='field-label'>{searchLabelText}</label>
                    <button
                        className={`ms-store-select__search-button ${isMapDisabled ? ` search-btn-disabled` : ``}`}
                        aria-label={searchButtonAriaLabel}
                        color={'primary'}
                        {...this.storeSearchAttributes}
                    >
                        {searchButtonText}
                    </button>
                </form>
                {/* VSI Customization - ends */}
                {locatorView && (
                    <button className='ms-store-select__toggle-view' onClick={onToggleListMapViewState} {...this.toggleMapViewAttributes}>
                        {toggleButtonText}
                    </button>
                )}
            </div>
        );
    }

    private _searchTextChanged = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const inputValue: string = e.target.value;

        await this.props.searchTermChanged(inputValue);
    };

    private _onSubmit = async (e: React.SyntheticEvent): Promise<void> => {
        e.preventDefault(); // Prevents form submission
        // VSI Customization - starts
        const { value } = this.props;
        // eslint-disable-next-line security/detect-unsafe-regex
        const re = /^[0-9]{5}(?:-[0-9]{4})?$/;
        if (value === '' || !re.test(value)) {
            await this.props.isErrorChanged(true);
        } else {
            // @ts-ignore
            if ('utag' in window) {
                const pageURL = MsDyn365.isBrowser && window.location.pathname;
                // @ts-ignore
                utag.link({
                    event: "store-locator-slideout-search",
                    event_category: "store locator slideout",
                    event_action: "search click",
                    event_label: `from: ${pageURL}`,
                    event_noninteraction: "false"
                });
            }
            this.searchBoxRef.current?.blur();
            await this.props.performSearch(this.props.value);
            await this.props.isErrorChanged(false);
        }
        // VSI Customization - ends
    };
}
